import axios from "axios";
import isArray from "lodash/isArray";

import ROUTES from "manager/config/apiRoutes";
import { API_URL } from "shared/lib/api/setupApi";
import { transformRoute } from "shared/utils/routing";
import { getAccessToken, getCachedActiveCompanySlug } from "shared/utils/auth";

const api = {
  addApplicationAttachments: async ({
    applicationId,
    file,
    metadata: { requiredAttachmentId },
  }) => {
    const formData = new FormData();
    const files = isArray(file) ? file : [file];
    files.forEach((newFile) => {
      formData.append("upload", newFile?.file || newFile);
      formData.append("is_public", Boolean(newFile?.isPublic));
    });
    if (requiredAttachmentId) {
      formData.append("attachment_request", `${requiredAttachmentId}`);
    }
    return axios
      .post(
        `${API_URL}${transformRoute(ROUTES.addAttachments, {
          applicationId,
        })}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      )
      .then(({ data }) => data);
  },
  deleteApplicationAttachments: async ({ applicationId, attachmentId }) =>
    axios.delete(
      `${API_URL}${transformRoute(ROUTES.deleteAttachments, {
        applicationId,
      })}`,
      {
        data: {
          attachments: isArray(attachmentId) ? attachmentId : [attachmentId],
        },
      }
    ),
  cancelAttachments: ({ applicationId }) =>
    axios.post(
      `${API_URL}${transformRoute(ROUTES.cancelAttachments, {
        applicationId,
      })}`
    ),
  confirmAttachments: ({ applicationId, attachmentsChangedVisiblility }) =>
    axios
      .post(
        `${API_URL}${transformRoute(ROUTES.confirmAttachments, {
          applicationId,
        })}`,
        attachmentsChangedVisiblility
      )
      .then(({ data }) => data),
  setBlockDealSubmitIfIncomeMissing: ({
    applicationId,
    blockDealSubmitIfIncomeDataIsMissing,
  }) =>
    axios
      .post(
        `${API_URL}${transformRoute(ROUTES.blockDealSubmitIfIncomeMissing, {
          applicationId,
        })}`,
        {
          blockDealSubmitIfIncomeDataIsMissing,
        }
      )
      .then(({ data }) => data),
  
  // Cosign Application APIs
  getCosignApplications: async ({ applicationId }) =>
    axios
      .get(
        `${API_URL}${transformRoute(ROUTES.cosignApplication, {
          applicationId,
        })}`
      )
      .then(({ data }) => data),
  
  createCosignApplication: async ({ applicationId }) =>
    axios
      .post(
        `${API_URL}${transformRoute(ROUTES.cosignApplication, {
          applicationId,
        })}`
      )
      .then(({ data }) => data),
  
  getCanInitiateCosign: async ({ applicationId }) =>
    axios
      .get(
        `${API_URL}${transformRoute(ROUTES.canInitiateCosign, {
          applicationId,
        })}`
      )
      .then(({ data }) => data),
  
  downloadApprovalLetter: async ({ cosignUuid }) => {
    const url = `${API_URL}${transformRoute(ROUTES.approvalLetter, { cosignUuid })}`;
    const token = getAccessToken();
    const companySlug = getCachedActiveCompanySlug();
    
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (companySlug) {
      headers['Company'] = companySlug;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });
    
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }
    
    const blob = await response.blob();
    
    // Extract filename from content-disposition header if available
    const contentDisposition = response.headers.get('content-disposition');
    let filename = `cosign-approval-letter-${cosignUuid}.pdf`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }
    
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    
    return blob;
  },
  cancelCosignApplication: async (cosignUuid) =>
    axios.post(`${API_URL}${transformRoute(ROUTES.cancelCosignApplication, { cosignUuid })}`)
      .then(({ data }) => data)
};


export default api;
